import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, CheckCircle, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlayerPasswordReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [membershipNumber, setMembershipNumber] = useState("");

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/api/academy/player/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!membershipNumber.trim()) {
      toast.error("Please enter your membership number");
      return;
    }
    
    try {
      setRequesting(true);
      await axios.post(`${API}/api/academy/player/request-password-reset`, {
        membership_number: membershipNumber
      });
      toast.success("If the membership number exists, a reset email will be sent to your parent.");
      setMembershipNumber("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request reset");
    } finally {
      setRequesting(false);
    }
  };

  // If we have a token, show the reset form
  if (token) {
    if (success) {
      return (
        <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Password Reset!</h2>
              <p className="text-slate-400 mb-6">
                Your password has been reset. You can now login with your membership number.
              </p>
              <Link to="/">
                <Button className="bg-yellow-500 hover:bg-yellow-400 text-black">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-400" />
            </div>
            <CardTitle className="text-white">Set Your Password</CardTitle>
            <CardDescription className="text-slate-400">
              Create a new password for your player account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label className="text-slate-300">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="bg-slate-900/50 border-slate-600 text-white pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="bg-slate-900/50 border-slate-600 text-white"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black"
              >
                {loading ? "Setting Password..." : "Set Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No token - show request reset form
  return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-amber-400" />
          </div>
          <CardTitle className="text-white">Player Password Reset</CardTitle>
          <CardDescription className="text-slate-400">
            Enter your membership number and we'll send a reset link to your parent's email
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <Label className="text-slate-300">Membership Number</Label>
              <Input
                type="text"
                value={membershipNumber}
                onChange={(e) => setMembershipNumber(e.target.value.toUpperCase())}
                placeholder="e.g., 15C0001"
                className="bg-slate-900/50 border-slate-600 text-white uppercase"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Your membership number is on your academy registration confirmation
              </p>
            </div>
            
            <Button
              type="submit"
              disabled={requesting}
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black"
            >
              {requesting ? "Sending..." : "Send Reset Link"}
            </Button>
            
            <div className="text-center">
              <Link to="/" className="text-sm text-blue-400 hover:text-blue-300">
                Back to Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
